import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';

/**
 * Row type consumed by the dashboard.
 * Sourced from AFib_Dashboard_Master.csv, plus supporting joins.
 */
export interface FlatRow {
  Patient_ID: string;
  DOB: string;
  Sex: string;
  Postal_Code: string;

  // ✅ NEW (from Master)
  Age_At_2025?: number | null;

  // Existing dashboard charts rely on these:
  Comorbidity_Count: number | null;
  MRDx_ICD10CA_Code: string | null;
  MRDx_ICD10CA_Display: string | null;

  Referral_ID: string;
  Referral_Date: string;

  Triage_Date: string | null;
  Assigned_Physician: string | null;

  Added_Date: string | null;

  // IMPORTANT: dashboard.ts uses Priority_Level
  Priority_Level: string | null;

  Current_Status: string | null;

  // ✅ NEW (from Master)
  Scheduled_Date?: string | null;

  Procedure_Date: string | null;
  Outcome: string | null;

  CHA2DS2_VASc_Score: number | null;

  // NEW KPI fields
  Wait1_Days?: number | null;
  Wait2_Days?: number | null;

  ED_Visits_AFib_2025?: number | null;
  Acute_Care_Cases_2025?: number | null;
  LOS_Admitted_Days_2025?: number | null;

  QoL_Baseline_AFEQT?: number | null;
  QoL_FollowUp_AFEQT?: number | null;
  QoL_Change?: number | null;
}

/** Encounter rows for real monthly ED / Acute utilization */
export interface EncounterRow {
  Encounter_ID: string;
  Patient_ID: string;
  Encounter_Type: string;         // "ED" or "Acute Care"
  Start_Date: string;             // YYYY-MM-DD
  End_Date: string;               // YYYY-MM-DD
  Encounter_ICD10CA_Code: string; // e.g. I48
  Encounter_Dx_Group: string;     // e.g. "Atrial fibrillation"
  LOS_Days: number;               // numeric
}

export type GenericRow = Record<string, string | null>;

@Injectable({ providedIn: 'root' })
export class DataService {
  private readonly PATHS = {
    master: 'assets/AFib_Dashboard_Master.csv',
    patient: 'assets/Patient.csv',
    patientComorbidities: 'assets/Patient_Comorbidities.csv',
    encounterHistory: 'assets/Encounter_History.csv',
    qualityOfLife: 'assets/Quality_of_Life.csv'
  } as const;

  constructor(private readonly http: HttpClient) {}

  /**
   * Keep existing method name so Dashboard doesn't need rewiring.
   * Returns rows from MASTER, joined with Patient + Comorbidity count.
   */
  async getFlatRows(): Promise<FlatRow[]> {
    const [masterCsv, patientCsv, comorbCsv] = await Promise.all([
      this.getCsvText(this.PATHS.master),
      this.getCsvText(this.PATHS.patient),
      this.getCsvText(this.PATHS.patientComorbidities)
    ]);

    const master = parseGenericCsv(masterCsv);
    const patients = parseGenericCsv(patientCsv);
    const comorbs = parseGenericCsv(comorbCsv);

    // Patient lookup (DOB/Sex/Postal)
    const patientById = new Map<string, GenericRow>();
    for (const p of patients) {
      const pid = p['Patient_ID'];
      if (pid) patientById.set(pid, p);
    }

    // Comorbidity counts per patient
    // Your updated file uses Comorbidity_Name; keep fallbacks for older versions.
    const comorbCountByPatient = new Map<string, number>();
    for (const c of comorbs) {
      const pid = c['Patient_ID'];
      if (!pid) continue;

      const name =
        (c['Comorbidity_Name'] ??
          c['Comorbidity'] ??
          c['Condition'] ??
          '')?.toString()
          .trim()
          .toLowerCase();

      if (!name || name === 'none') continue;

      comorbCountByPatient.set(pid, (comorbCountByPatient.get(pid) ?? 0) + 1);
    }

    // Build rows in the shape Dashboard expects
    return master.map((m): FlatRow => {
      const pid = m['Patient_ID'] ?? '';
      const p = patientById.get(pid);

      const priority = m['Priority']; // Master uses "Priority" (Urgent/Priority/Elective)
      const outcome = m['Outcome'];
      const currentStatus = m['Current_Status'];

      const score = toNumber(m['CHA2DS2_VASc'] ?? null);

      // Friendly Dx display
      const dxCode = m['Primary_Dx_ICD10CA'] ?? null;
      const dxDisplay = dxCode ? 'Atrial fibrillation' : null;

      return {
        Patient_ID: pid,
        DOB: (p?.['DOB'] ?? '') as string,
        Sex: (p?.['Sex'] ?? '') as string,
        Postal_Code: (p?.['Postal_Code'] ?? '') as string,

        // ✅ NEW: Age from Master
        Age_At_2025: toNumber(m['Age_At_2025'] ?? null),

        Comorbidity_Count: comorbCountByPatient.get(pid) ?? 0,
        MRDx_ICD10CA_Code: dxCode,
        MRDx_ICD10CA_Display: dxDisplay,

        Referral_ID: (m['Referral_ID'] ?? '') as string,
        Referral_Date: (m['Referral_Date'] ?? '') as string,

        Triage_Date: (m['Triage_Date'] ?? null) as string | null,
        Assigned_Physician: (m['Assigned_Physician'] ?? null) as string | null,

        Added_Date: (m['Added_Date'] ?? null) as string | null,

        Priority_Level: (priority ?? null) as string | null,

        Current_Status: (currentStatus ?? null) as string | null,

        // ✅ NEW: Scheduled date from Master
        Scheduled_Date: (m['Scheduled_Date'] ?? null) as string | null,

        Procedure_Date: (m['Procedure_Date'] ?? null) as string | null,
        Outcome: (outcome ?? null) as string | null,

        CHA2DS2_VASc_Score: score,

        // New KPI fields
        Wait1_Days: toNumber(m['Wait1_Days'] ?? null),
        Wait2_Days: toNumber(m['Wait2_Days'] ?? null),

        ED_Visits_AFib_2025: toNumber(m['ED_Visits_AFib_2025'] ?? null),
        Acute_Care_Cases_2025: toNumber(m['Acute_Care_Cases_2025'] ?? null),
        LOS_Admitted_Days_2025: toNumber(m['LOS_Admitted_Days_2025'] ?? null),

        QoL_Baseline_AFEQT: toNumber(m['QoL_Baseline_AFEQT'] ?? null),
        QoL_FollowUp_AFEQT: toNumber(m['QoL_FollowUp_AFEQT'] ?? null),
        QoL_Change: toNumber(m['QoL_Change'] ?? null)
      };
    });
  }

  /**
   * NEW: Load encounter rows for real monthly ED / Acute trends.
   */
  async getEncounterRows(): Promise<EncounterRow[]> {
    const csv = await this.getCsvText(this.PATHS.encounterHistory);
    const rows = parseGenericCsv(csv);

    return rows.map((r): EncounterRow => ({
      Encounter_ID: (r['Encounter_ID'] ?? '') as string,
      Patient_ID: (r['Patient_ID'] ?? '') as string,
      Encounter_Type: (r['Encounter_Type'] ?? '') as string,
      Start_Date: (r['Start_Date'] ?? '') as string,
      End_Date: (r['End_Date'] ?? '') as string,
      Encounter_ICD10CA_Code: (r['Encounter_ICD10CA_Code'] ?? '') as string,
      Encounter_Dx_Group: (r['Encounter_Dx_Group'] ?? '') as string,
      LOS_Days: toNumber(r['LOS_Days'] ?? null) ?? 0
    }));
  }

  // -------------------- internal helpers --------------------

  private async getCsvText(path: string): Promise<string> {
    return await firstValueFrom(this.http.get(path, { responseType: 'text' }));
  }
}

// ------------------- parsing -------------------

/**
 * Small CSV parser that handles quoted values with commas.
 * (Still lightweight; good enough for our synthetic CSVs.)
 */
function parseGenericCsv(text: string): GenericRow[] {
  const lines = text.split(/\r?\n/).filter(l => l.trim().length > 0);
  if (lines.length < 2) return [];

  const header = splitCsvLine(lines[0]).map(h => h.trim());
  const rows: GenericRow[] = [];

  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    if (!line.trim()) continue;

    const cells = splitCsvLine(line);
    const row: GenericRow = {};

    header.forEach((h, idx) => {
      const raw = (cells[idx] ?? '').trim();
      // strip surrounding quotes if present
      const cleaned = raw.startsWith('"') && raw.endsWith('"') ? raw.slice(1, -1) : raw;
      row[h] = cleaned === '' ? null : cleaned;
    });

    rows.push(row);
  }

  return rows;
}

/** Split one CSV line into cells, respecting quotes */
function splitCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];

    if (ch === '"') {
      // handle escaped quotes ""
      const next = line[i + 1];
      if (inQuotes && next === '"') {
        cur += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (ch === ',' && !inQuotes) {
      out.push(cur);
      cur = '';
      continue;
    }

    cur += ch;
  }

  out.push(cur);
  return out;
}

function toNumber(value: string | null): number | null {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isNaN(n) ? null : n;
}
